import styled from "styled-components";
import {
  OnestNormalSmall,
  OnestSemiBoldDefault,
} from "../../../components/styled/TextComponents";
import {
  color_grey_dark,
  color_grey_ultra_dark,
  color_red_default,
  color_white,
} from "../../../constants/colors";

export const ProgramPageWrap = styled.div`
  width: 100%;
  align-self: flex-start;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const ProgramHeaderBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 100%;
`;

export const ProgramHeaderCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

export const ProgramTitle = styled(OnestSemiBoldDefault)`
  color: ${color_grey_ultra_dark};
  font-size: 32px;
  line-height: 1.15;
`;

export const ProgramSubtitle = styled(OnestNormalSmall)`
  color: ${color_grey_dark};
  font-size: 15px;
`;

export const MapScrollWrap = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  -webkit-overflow-scrolling: touch;
`;

export const MapContainer = styled.div`
  position: relative;
  width: 100%;
  min-width: 820px;
  height: min(72vh, 700px);
  min-height: 580px;
  border-radius: 24px;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.65);
`;

export const MapBackdrop = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 24px;
  overflow: hidden;
  background: url("/program/background/background-sky.png") center bottom /
    cover no-repeat;
  pointer-events: none;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.27);
  }
`;

export const MapInner = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  padding-bottom: 48px;
  box-sizing: border-box;
`;

export const MapSvg = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
`;

export const MapNodeColumn = styled.div`
  position: absolute;
  left: ${(props) => props.$x}%;
  top: ${(props) => props.$y}%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  z-index: ${(props) => props.$z || 2};
  width: ${(props) => props.$captionWidth || 148}px;
  pointer-events: none;
  will-change: transform;

  &:has(button:hover),
  &:has(button:focus-visible) {
    z-index: 10;
  }
`;

export const MapNodeImage = styled.button`
  position: relative;
  width: ${(props) => props.$size}px;
  height: ${(props) => props.$size}px;
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  pointer-events: ${(props) => (props.$interactive ? "auto" : "none")};
  cursor: ${(props) => (props.$interactive ? "pointer" : "default")};
  transition:
    transform 0.2s ease,
    filter 0.2s ease;
  filter: drop-shadow(0 12px 20px rgba(54, 54, 54, 0.18));

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
    pointer-events: none;
  }

  &:hover,
  &:focus-visible {
    transform: ${(props) =>
      props.$interactive ? "translateY(-10px)" : "none"};
    filter: ${(props) =>
      props.$interactive
        ? "drop-shadow(0 18px 26px rgba(240, 79, 79, 0.32))"
        : "drop-shadow(0 12px 20px rgba(54, 54, 54, 0.18))"};
  }

  &:focus-visible {
    outline: 3px solid rgba(240, 79, 79, 0.28);
    outline-offset: 3px;
    border-radius: 50%;
  }
`;

export const NodeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 11px;
  margin-top: 8px;
  margin-bottom: 6px;
  transform: ${(props) => (props.$tight ? "translateY(20px)" : "none")};
  border-radius: 999px;
  background: ${(props) => (props.$accent ? color_red_default : color_white)};
  color: ${(props) => (props.$accent ? color_white : color_red_default)};
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  box-shadow: 0 3px 10px rgba(54, 54, 54, 0.09);
  pointer-events: none;
  flex-shrink: 0;
`;

export const NodeCaptionBlock = styled.div`
  width: ${(props) => props.$width || 148}px;
  margin-top: 0px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  pointer-events: none;
  flex-shrink: 0;
`;

export const NodeTitle = styled(OnestSemiBoldDefault)`
  font-size: 14px;
  line-height: 1.25;
  text-align: center;
  color: #2b2b2b;
`;

export const NodeSubtitle = styled(OnestNormalSmall)`
  font-size: 11px;
  line-height: 1.35;
  text-align: center;
  color: ${color_grey_dark};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const FooterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: stretch;
  gap: 16px;
  width: 100%;
  padding: 16px 22px;
  background: ${color_white};
  border-radius: 20px;
  box-sizing: border-box;
  box-shadow: 0 6px 24px rgba(54, 54, 54, 0.06);
`;

export const FooterStats = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 32px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    gap: 14px;
  }
`;

export const FooterStat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const FooterStatTitle = styled(OnestSemiBoldDefault)`
  font-size: 14px;
  color: ${color_grey_ultra_dark};
`;

export const FooterStatText = styled(OnestNormalSmall)`
  font-size: 12px;
  color: ${color_grey_dark};
`;
